// Core
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Libraries
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

// Store
import {
  getAuthToken,
  getCustomer,
  getUser,
} from '../../../store/session.store';

// Types
import {
  CrusherOperationalData,
  CrusherWebsocketRequestEvents,
  CrusherWebsocketResponseEvents,
  EquipmentDataReadStatus,
  EquipmentUnitCrusher,
  EquipmentUnitDataConeCrusher,
  EquipmentUnitDataVSICrusher,
  SchemaType,
} from '../../../types/crusher';
import {
  Customer,
  EdgeDeviceOperationalStatus,
  EdgeDeviceOperationalStatusStatus,
  EquipmentCategory,
  GPS,
  Site,
  User,
  UserType,
} from '../../../types/global';

// Plugins
import { RequestMethod, requestVantageApi } from '../../../plugins/request';
import {
  DEVICE_COMMUNICATION_THRESHOLD,
  extractGpsFromLatestUnitData,
  getSchemaType,
  lastOperationalEdgeStatus,
  lastOperationalUnitDataCone,
  lastOperationalUnitDataVSI,
} from '../../../plugins/crusher';
import {
  getSocket,
  Socket,
  SocketDefaultListenEvents,
} from '../../../plugins/socket';

// Locale
import i18n from '../../../locale/i18n';

// Components
import { CustomerPanel } from '../../../layout/CustomerPanel';
import { Loader, LoaderSize } from '../../../layout/elements/Loader';
import { showToast, ToastType } from '../../../layout/elements/Toast';

// Views
import { VSI } from './VSI';
import { Patriot } from './Patriot';
import { LoaderContainer } from './Dashboard.style';

function CrusherDashboard(): JSX.Element | null {
  /* ***********************************************************************************************
  ******************************************* GETTERS **********************************************
  *********************************************************************************************** */

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { idEquipmentUnit }: {
    idEquipmentUnit: string,
  } = useParams() as { idEquipmentUnit: string };
  const user: User = useMemo(() => getUser() as User, []);
  const customer: Customer = useMemo(() => getCustomer() as Customer, []);
  const authToken: string = useMemo(() => getAuthToken() as string, []);
  const isScreenMounted = useRef<boolean>(false);

  /* ***********************************************************************************************
  ******************************************* STATE ***********************************************
  *********************************************************************************************** */

  type State = {
    equipmentUnitCrusher: EquipmentUnitCrusher | null;
    isLoading: boolean;
    lastLidOpenedAt: number | null;
    operationalData: CrusherOperationalData | null;
    site: Site | null;
    socket: Socket | null;
    unitCoordinates: GPS | null;
  };

  const initialState: State = {
    equipmentUnitCrusher: null,
    isLoading: true,
    lastLidOpenedAt: null,
    operationalData: null,
    site: null,
    socket: null,
    unitCoordinates: null,
  };

  const [state, setState] = useState<State>(initialState);

  /* ***********************************************************************************************
  ******************************************* METHODS **********************************************
  *********************************************************************************************** */

  const fetchPlantData = useCallback(
    async (equipmentUnitCrusher: EquipmentUnitCrusher): Promise<Site | null> => (
      requestVantageApi.plant({
        method: RequestMethod.GET,
        path: `/site/${equipmentUnitCrusher.idSite}`,
        params: (user.type === UserType.admin || user.type === UserType.service)
          ? { idCustomer: customer.id }
          : {},
      })
    ),
    [customer, user],
  );

  const fetchEquipmentUnit = useCallback(async (): Promise<EquipmentUnitCrusher> => {
    const response: EquipmentUnitCrusher = await requestVantageApi.equipmentUnit({
      method: RequestMethod.GET,
      path: `/crusher/${idEquipmentUnit}`,
      params: (user.type === UserType.admin || user.type === UserType.service)
        ? { idCustomer: customer.id }
        : {},
    });

    // If the response is null, fallback to the plant management page
    if (!response) {
      navigate('/plant-management');
    }

    return response;
  }, [idEquipmentUnit, customer, user, navigate]);

  const fetchLatestUnitData = useCallback(async ():
  Promise<CrusherOperationalData> => {
    const response: (
      CrusherOperationalData
    ) = await requestVantageApi.equipmentUnitDataMonitoring({
      method: RequestMethod.GET,
      path: `/crusher/latest_data/${idEquipmentUnit}`,
      params: (user.type === UserType.admin || user.type === UserType.service)
        ? { idCustomer: customer.id }
        : {},
    });

    return response;
  }, [idEquipmentUnit, customer, user]);

  const requestCrusherData = useCallback((): void => {
    if (!state.socket) {
      throw new Error('Socket connection is not established');
    }

    // Send real-time crusher data request
    state.socket.emit(CrusherWebsocketRequestEvents.realTimeCrusherData, {
      interval: 10000, // 10 seconds
      idEquipmentUnit,
      equipmentCategory: EquipmentCategory.crusher,
      idCustomer: (
        user.type === UserType.admin || user.type === UserType.service
      ) && customer.id
        ? customer.id
        : null,
      authToken,
      dataRange: 1000 * 60 * 1,
    });
  }, [
    idEquipmentUnit,
    user,
    customer,
    authToken,
    state.socket,
  ]);

  const setupSocketListeners = useCallback((): void => {
    if (state.socket) {
      // Listen the connect event
      state.socket.on(SocketDefaultListenEvents.connect, (): void => {
        requestCrusherData();
      });

      // Listen the EquipmentUnitCrusherData event
      state.socket.on(
        CrusherWebsocketResponseEvents.equipmentUnitCrusherData,
        (data: CrusherOperationalData): void => {
          if (!data || data.length === 0) {
            return;
          }

          setState((currentState: State): State => {
            // Manage operational data updates
            let updatedOperationalData: CrusherOperationalData;

            const hasOperationalData: boolean = data.some((item) => 'readStatus' in item);
            const previousData: CrusherOperationalData = currentState.operationalData || [];

            if (hasOperationalData) {
              updatedOperationalData = [...data];
            } else {
              const previousOperationalOnly: CrusherOperationalData = previousData.filter(
                (item) => !('edgeStatus' in item),
              );
              updatedOperationalData = [...previousOperationalOnly, ...data];
            }

            // Manage GPS updates
            const updatedUnitCoordinates: GPS | null = extractGpsFromLatestUnitData(data);

            // Update lastLidOpenedAt while lid is open (false) for VSI
            const currentSchemaType: SchemaType | undefined = getSchemaType(
              currentState.equipmentUnitCrusher,
            );
            let updatedLastLidOpenedAt: number | null = currentState.lastLidOpenedAt;

            if (currentSchemaType === SchemaType.vsi) {
              const currentLidStatus: boolean | null | undefined = (
                lastOperationalUnitDataVSI(updatedOperationalData) as
                  EquipmentUnitDataVSICrusher | null
              )?.lidOpenStatus;

              if (currentLidStatus === false) {
                const payloadTimestamp: number | null | undefined = (
                  lastOperationalUnitDataVSI(updatedOperationalData) as
                    EquipmentUnitDataVSICrusher | null
                )?.timestamp;
                if (payloadTimestamp) {
                  updatedLastLidOpenedAt = payloadTimestamp;
                }
              }
            }

            return {
              ...currentState,
              lastLidOpenedAt: updatedLastLidOpenedAt,
              operationalData: updatedOperationalData,
              unitCoordinates: (
                currentState.equipmentUnitCrusher?.gpsEnabled && updatedUnitCoordinates
                  ? updatedUnitCoordinates
                  : currentState.unitCoordinates
              ),
            };
          });
        },
      );

      state.socket.on(SocketDefaultListenEvents.disconnect, (): void => {});
    }
  }, [requestCrusherData, state.socket]);

  const connectSocket = useCallback((): Promise<void> => new Promise((resolve) => {
    try {
      // Create socket connection
      const socket: Socket = getSocket({
        url: String(process.env.REACT_APP_EQUIPMENT_UNIT_DATA_MONITORING_API),
        token: authToken,
      });

      // Start socket connection
      socket.connect();

      // Set socket state
      setState((currentState: State): State => ({
        ...currentState,
        socket,
      }));

      resolve();
    } catch (error: unknown) {
      showToast({ type: ToastType.error, text: `${i18n.t('common.defaultError')}` });
      resolve();
    }
  }), [authToken]);

  const initializeDashboardRequest = useCallback(async (): Promise<void> => {
    setState((prevState: State) => ({
      ...prevState,
      isLoading: true,
    }));

    try {
      const equipmentUnitRequest: Promise<EquipmentUnitCrusher> = fetchEquipmentUnit();
      const latestUnitDataRequest: Promise<CrusherOperationalData> = fetchLatestUnitData();
      const socketRequest: Promise<void> = connectSocket();

      const results: [
        PromiseSettledResult<EquipmentUnitCrusher>,
        PromiseSettledResult<CrusherOperationalData>,
        PromiseSettledResult<void>,
      ] = await Promise.allSettled([
        equipmentUnitRequest,
        latestUnitDataRequest,
        socketRequest,
      ]);

      const [equipmentUnitResult, latestUnitDataResult] = results;

      // Update state based on specific request results
      if (equipmentUnitResult.status === 'fulfilled') {
        const equipmentUnit: EquipmentUnitCrusher = equipmentUnitResult.value;

        setState((prevState: State) => ({
          ...prevState,
          equipmentUnitCrusher: equipmentUnit,
        }));

        // Fetch plant data after equipment unit is loaded
        const plantRequest: Site | null = await fetchPlantData(equipmentUnit);
        if (plantRequest) {
          setState((prevState: State) => ({
            ...prevState,
            site: plantRequest,
          }));
        }

        // Initial location management
        let initialLocation: GPS | null = null;

        if (equipmentUnit.gpsEnabled) {
          // GPS ENABLED: try to extract GPS data from latestUnitData
          if (latestUnitDataResult.status === 'fulfilled') {
            const latestGpsData: GPS | null = extractGpsFromLatestUnitData(
              latestUnitDataResult.value,
            );

            if (
              !_.isNil(latestGpsData?.lat)
              && !_.isNil(latestGpsData?.lng)
              && !_.isNil(latestGpsData?.gpsTimestamp)
            ) {
              // Valid GPS data from latestUnitData → use complete GPS type
              initialLocation = {
                lat: latestGpsData?.lat,
                lng: latestGpsData?.lng,
                gpsTimestamp: latestGpsData?.gpsTimestamp,
              };
            } else if (!_.isNil(equipmentUnit.lat) && !_.isNil(equipmentUnit.lng)) {
              // Fallback: GPS enabled but no data in latestUnitData → use static coordinates
              initialLocation = {
                lat: equipmentUnit.lat,
                lng: equipmentUnit.lng,
              };
            }
          } else if (!_.isNil(equipmentUnit.lat) && !_.isNil(equipmentUnit.lng)) {
            // Fallback: GPS enabled but latestUnitData failed → use static coordinates
            initialLocation = {
              lat: equipmentUnit.lat,
              lng: equipmentUnit.lng,
            };
          }
        } else if (!_.isNil(equipmentUnit.lat) && !_.isNil(equipmentUnit.lng)) {
          // GPS DISABLED: use static coordinates (CoordinatesType)
          initialLocation = {
            lat: equipmentUnit.lat,
            lng: equipmentUnit.lng,
          };
        }

        // Set initial location for the pin and map
        if (initialLocation) {
          setState((prevState: State) => ({
            ...prevState,
            unitCoordinates: initialLocation,
          }));
        }
      }

      // Set latest unit data as operational data for the first render
      if (latestUnitDataResult.status === 'fulfilled') {
        const latestData: CrusherOperationalData | undefined = latestUnitDataResult.value;

        // Extract lastLidOpenedAt from latest data (VSI only)
        let extractedLastLidOpenedAt: number | null = null;
        if (latestData) {
          const edgeStatusEntry = latestData.find(
            (item) => 'lastLidOpenedAt' in item,
          );
          if (edgeStatusEntry && 'lastLidOpenedAt' in edgeStatusEntry) {
            extractedLastLidOpenedAt = edgeStatusEntry.lastLidOpenedAt ?? null;
          }
        }

        setState((prevState: State) => ({
          ...prevState,
          lastLidOpenedAt: extractedLastLidOpenedAt,
          operationalData: latestData ?? null,
        }));
      }

      // Errors are handled silently as requested
    } catch (error: unknown) {
      // Handle errors silently or implement error handling as needed
    } finally {
      setState((prevState: State) => ({
        ...prevState,
        isLoading: false,
      }));
    }
  }, [
    connectSocket,
    fetchEquipmentUnit,
    fetchLatestUnitData,
    fetchPlantData,
  ]);

  /* ***********************************************************************************************
  *************************************** COMPONENT HANDLING ***************************************
  *********************************************************************************************** */

  // Schema-aware detection - replaces model-based logic
  const schemaType: SchemaType | undefined = getSchemaType(state.equipmentUnitCrusher);

  const lastUnitData: (
    EquipmentUnitDataVSICrusher | EquipmentUnitDataConeCrusher | null
  ) = useMemo(() => {
    let result: EquipmentUnitDataVSICrusher | EquipmentUnitDataConeCrusher | null = null;

    if (!state.equipmentUnitCrusher || !state.operationalData) return null;

    if (schemaType === SchemaType.vsi) {
      result = lastOperationalUnitDataVSI(state.operationalData);
    } else if (schemaType === SchemaType.patriot) {
      result = lastOperationalUnitDataCone(state.operationalData);
    }

    return result;
  }, [schemaType, state.equipmentUnitCrusher, state.operationalData]);

  const lastEdgeStatus: EdgeDeviceOperationalStatus | null = lastOperationalEdgeStatus(
    state.operationalData,
  );

  const alertText: string | null = useMemo<string | null>(() => {
    const lastDataTimestamp: number | undefined | null = lastUnitData?.timestamp;
    const lastReadStatus: EquipmentDataReadStatus | undefined | null = lastUnitData?.readStatus;
    const edgeStatus: EdgeDeviceOperationalStatusStatus | undefined | null = (
      lastEdgeStatus?.edgeStatus
    );

    let text: string | null = null;

    if (edgeStatus === EdgeDeviceOperationalStatusStatus.off) {
      text = t('view.crusher.alert.deviceOffline');
    } else if (
      lastReadStatus === EquipmentDataReadStatus.invalid
    ) {
      text = t('view.crusher.alert.plcCommunicationError');
    } else if (
      lastDataTimestamp
      && (Date.now() - lastDataTimestamp) > DEVICE_COMMUNICATION_THRESHOLD
      && lastReadStatus === EquipmentDataReadStatus.valid
    ) {
      text = t('view.crusher.alert.communicationError');
    }

    return text;
  }, [
    t,
    lastUnitData,
    lastEdgeStatus,
  ]);

  useEffect((): void => {
    if (isScreenMounted.current) {
      return;
    }

    isScreenMounted.current = true;

    initializeDashboardRequest();
  }, [initializeDashboardRequest]);

  /* Socket event listeners */
  useEffect((): void => {
    setupSocketListeners();
  }, [setupSocketListeners]);

  /* Clean up socket connection when component unmounts */
  useEffect((): (() => void) => (): void => {
    if (state.socket) {
      state.socket.off(SocketDefaultListenEvents.connect);
      state.socket.off(CrusherWebsocketResponseEvents.equipmentUnitCrusherData);
      state.socket.off(SocketDefaultListenEvents.disconnect);
      state.socket.disconnect();
    }
  }, [state.socket]);

  /* Render the appropriate dashboard based on equipment schema */
  const renderDashboard = useCallback((): JSX.Element | null => {
    // Schema validation with error handling
    if (_.isNil(schemaType) && state.equipmentUnitCrusher) {
      showToast({
        type: ToastType.error,
        text: i18n.t('view.crusher.error.unsupportedSchema', {
          plcTagSchema: state.equipmentUnitCrusher.plcTagSchema,
          dataSchema: state.equipmentUnitCrusher.dataSchema,
          model: state.equipmentUnitCrusher.model,
        }),
      });

      // Navigate back to plant management
      navigate('/plant-management');
    }

    // Schema-aware rendering
    switch (schemaType) {
      case SchemaType.patriot:
        return (
          <Patriot
            alertText={alertText}
            equipmentUnitCrusher={state.equipmentUnitCrusher as EquipmentUnitCrusher}
            operationalData={state.operationalData as CrusherOperationalData}
            site={state.site as Site}
            unitCoordinates={state.unitCoordinates as GPS}
            testId="patriot-dashboard"
          />
        );
      case SchemaType.vsi:
        return (
          <VSI
            alertText={alertText}
            lastLidOpenedAt={state.lastLidOpenedAt}
            operationalData={state.operationalData as CrusherOperationalData}
            equipmentUnitCrusher={state.equipmentUnitCrusher as EquipmentUnitCrusher}
            site={state.site as Site}
            unitCoordinates={state.unitCoordinates as GPS}
            testId="vsi-dashboard"
          />
        );
      default:
        // This should never happen due to validation above
        return null;
    }
  }, [
    alertText,
    state.lastLidOpenedAt,
    state.operationalData,
    state.equipmentUnitCrusher,
    state.site,
    state.unitCoordinates,
    schemaType,
    navigate,
  ]);

  return (
    <CustomerPanel testId="crusher-dashboard">
      {
        state.isLoading
          ? (
            <LoaderContainer data-testid="loader-container">
              <Loader text={`${t('common.loader')}`} size={LoaderSize.xl} />
            </LoaderContainer>
          ) : (
            renderDashboard()
          )
        }
    </CustomerPanel>
  );
}

export { CrusherDashboard };
